'use client';

import type { FragmentType } from '@packages/gql/generated/shopping/fragment-masking';
import type { InstallmentCalculatorCallToActionFragmentFragmentDoc } from '@packages/gql/generated/shopping/InstallmentCalculatorCallToActionFragmentFragmentDoc';
import { unmask } from '@packages/gql/src/betterMasking';
import { useEffect, useState } from 'react';
import { InstallmentCalculatorDialog } from '@packages/installment-calculator/src/InstallmentCalculatorDialog/InstallmentCalculatorDialog.client';
import { useAbTesting } from '@packages/shared/src/abtesting/useAbTesting/useAbTesting';
import { useInstallmentCalculatorTracking } from '../../../../Tracking/hooks/useInstallmentCalculatorTracking/useInstallmentCalculatorTracking';
import { OutlinedWithBadge } from '../OutlinedWithBadge/OutlinedWithBadge.client';

export type OutlinedWithBadgeWithDialogProps = {
  /**
   * PromotionFragment data fragment
   */
  installmentData: FragmentType<typeof InstallmentCalculatorCallToActionFragmentFragmentDoc>;
  /**
   * Product price value as float (e.g. 13.37)
   */
  basePrice: number;
  abTestId: string;
};

const Content = ({
  basePrice,
  numberOfInstallments,
  singleInstallment,
  currency,
  abTestId,
}: {
  basePrice: number;
  numberOfInstallments: number;
  singleInstallment: number;
  currency: string;
  abTestId: string;
}) => {
  const [dialogOpened, setDialogOpened] = useState(false);

  const { setOutcome } = useAbTesting();
  const { trackGlycerinDisplayLink } = useInstallmentCalculatorTracking();

  useEffect(() => {
    trackGlycerinDisplayLink();
    setOutcome(abTestId, { RATE_SEEN: 1 });
  }, [trackGlycerinDisplayLink, setOutcome, abTestId]);

  const handleOpenDialog = () => {
    setOutcome(abTestId, { RATE_CLICKED: 1 });
    setDialogOpened(true);
  };

  return (
    <>
      <InstallmentCalculatorDialog
        basePrice={basePrice}
        isOpen={dialogOpened}
        onClose={() => setDialogOpened(false)}
      />
      <OutlinedWithBadge
        singleInstallment={singleInstallment}
        currency={currency}
        numberOfInstallments={numberOfInstallments}
        openInstallmentCalculatorHandler={handleOpenDialog}
      />
    </>
  );
};

export const OutlinedWithBadgeWithDialog = ({
  installmentData,
  basePrice,
  abTestId,
}: OutlinedWithBadgeWithDialogProps) => {
  const { numberOfInstallments, singleInstallment, currency } =
    unmask<typeof InstallmentCalculatorCallToActionFragmentFragmentDoc>(installmentData);

  if (!singleInstallment?.value) {
    return null;
  }

  return (
    <Content
      basePrice={basePrice}
      numberOfInstallments={numberOfInstallments}
      singleInstallment={singleInstallment.value}
      currency={currency}
      abTestId={abTestId}
    />
  );
};
